import { useEffect, useRef, useState } from 'react';
import { playHaptic } from '../haptics';
import { LOGOS } from '../logos';
import { addBalance } from '../balance';
import { playResultSound, stopResultSounds } from '../sounds';
import { playCue } from '../cuelume';
import { ResultActions, ResultSticker } from '../components/GameHeader';

const img = (name) => `${process.env.PUBLIC_URL}/img/${name}`;

const SIZE = 6;
const MOVES_LIMIT = 20;
const SWAP_MS = 240; // must match the transform transition in CSS
const FALL_MS = 430; // must match the tile-drop animation in CSS
const CLEAR_DELAY = 300;
const REVERT_HOLD = 120;
const POINTS_PER_TILE = 10;
const SWIPE_THRESHOLD = 18;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function randomLogo(exclude = []) {
  const pool = LOGOS.filter((logo) => !exclude.includes(logo.id));
  return pool[Math.floor(Math.random() * pool.length)];
}

// Board without ready-made matches so the first move is always the player's
function createBoard(uidRef) {
  const cells = [];
  for (let i = 0; i < SIZE * SIZE; i += 1) {
    const col = i % SIZE;
    const row = Math.floor(i / SIZE);
    const exclude = [];
    if (col >= 2 && cells[i - 1].logoId === cells[i - 2].logoId) {
      exclude.push(cells[i - 1].logoId);
    }
    if (row >= 2 && cells[i - SIZE].logoId === cells[i - 2 * SIZE].logoId) {
      exclude.push(cells[i - SIZE].logoId);
    }
    const logo = randomLogo(exclude);
    uidRef.current += 1;
    cells.push({ uid: uidRef.current, logoId: logo.id, src: logo.src });
  }
  return cells;
}

function findMatches(board) {
  const matched = new Set();

  for (let row = 0; row < SIZE; row += 1) {
    let run = 1;
    for (let col = 1; col <= SIZE; col += 1) {
      const same =
        col < SIZE &&
        board[row * SIZE + col].logoId === board[row * SIZE + col - 1].logoId;
      if (same) {
        run += 1;
      } else {
        if (run >= 3) {
          for (let k = 0; k < run; k += 1) matched.add(row * SIZE + col - 1 - k);
        }
        run = 1;
      }
    }
  }

  for (let col = 0; col < SIZE; col += 1) {
    let run = 1;
    for (let row = 1; row <= SIZE; row += 1) {
      const same =
        row < SIZE &&
        board[row * SIZE + col].logoId === board[(row - 1) * SIZE + col].logoId;
      if (same) {
        run += 1;
      } else {
        if (run >= 3) {
          for (let k = 0; k < run; k += 1) matched.add((row - 1 - k) * SIZE + col);
        }
        run = 1;
      }
    }
  }

  return matched;
}

// Drop the survivors down each column and refill the gaps from the top.
// Survivors keep their uid so React reuses the DOM node and the transform
// transition animates the fall; new tiles get a `drop` distance (in cells)
// so CSS can spawn them above the board and let them fall in.
function collapse(board, matched, uidRef) {
  const next = board.map((tile, index) => (matched.has(index) ? null : tile));
  for (let col = 0; col < SIZE; col += 1) {
    const survivors = [];
    for (let row = SIZE - 1; row >= 0; row -= 1) {
      const tile = next[row * SIZE + col];
      if (tile) survivors.push({ uid: tile.uid, logoId: tile.logoId, src: tile.src });
    }
    let row = SIZE - 1;
    for (const tile of survivors) {
      next[row * SIZE + col] = tile;
      row -= 1;
    }
    const newCount = row + 1;
    for (; row >= 0; row -= 1) {
      const logo = randomLogo();
      uidRef.current += 1;
      next[row * SIZE + col] = {
        uid: uidRef.current,
        logoId: logo.id,
        src: logo.src,
        drop: newCount,
      };
    }
  }
  return next;
}

function isAdjacent(a, b) {
  const rowA = Math.floor(a / SIZE);
  const colA = a % SIZE;
  const rowB = Math.floor(b / SIZE);
  const colB = b % SIZE;
  return Math.abs(rowA - rowB) + Math.abs(colA - colB) === 1;
}

// Neighbor in the dominant swipe direction, or null at the board edge
function neighborByDelta(index, dx, dy) {
  const row = Math.floor(index / SIZE);
  const col = index % SIZE;
  if (Math.abs(dx) > Math.abs(dy)) {
    const nextCol = col + (dx > 0 ? 1 : -1);
    if (nextCol < 0 || nextCol >= SIZE) return null;
    return row * SIZE + nextCol;
  }
  const nextRow = row + (dy > 0 ? 1 : -1);
  if (nextRow < 0 || nextRow >= SIZE) return null;
  return nextRow * SIZE + col;
}

function Match3({ onExit }) {
  const uidRef = useRef(0);
  const dragRef = useRef(null);
  const [board, setBoard] = useState(() => createBoard(uidRef));
  const [selected, setSelected] = useState(null);
  const [clearing, setClearing] = useState(() => new Set());
  const [score, setScore] = useState(0);
  const [movesLeft, setMovesLeft] = useState(MOVES_LIMIT);
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);
  const resultSettled = useRef(false);

  useEffect(() => () => stopResultSounds(), []);

  useEffect(() => {
    if (!finished || resultSettled.current) return;
    resultSettled.current = true;
    playResultSound('win');
    // Score earned in a finished run tops up the site-wide balance
    if (score > 0) addBalance(score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const restart = () => {
    setBoard(createBoard(uidRef));
    setSelected(null);
    setClearing(new Set());
    setScore(0);
    setMovesLeft(MOVES_LIMIT);
    setBusy(false);
    setFinished(false);
    resultSettled.current = false;
    stopResultSounds();
    playHaptic('selection');
  };

  const resolveCascades = async (startBoard) => {
    let current = startBoard;
    let chain = 1;
    let gained = 0;

    for (;;) {
      const matches = findMatches(current);
      if (matches.size === 0) break;

      playHaptic('success');
      playCue(chain === 1 ? 'sparkle' : 'chime');
      setClearing(matches);
      await sleep(CLEAR_DELAY);

      const points = matches.size * POINTS_PER_TILE * chain;
      gained += points;
      current = collapse(current, matches, uidRef);
      setClearing(new Set());
      setBoard(current);
      setScore((s) => s + points);

      chain += 1;
      // Let the falling and refill animations land before the next wave
      await sleep(FALL_MS);
    }

    return gained;
  };

  const trySwap = async (from, to) => {
    if (busy || finished) return;
    setBusy(true);

    const swapped = [...board];
    [swapped[from], swapped[to]] = [swapped[to], swapped[from]];

    // Show the swap slide first, then decide what happens
    setBoard(swapped);
    await sleep(SWAP_MS);

    if (findMatches(swapped).size === 0) {
      // Useless swap: the tiles slide back to where they were
      playHaptic('error');
      playCue('error');
      await sleep(REVERT_HOLD);
      setBoard(board);
      await sleep(SWAP_MS);
      setBusy(false);
      return;
    }

    const movesAfter = movesLeft - 1;
    setMovesLeft(movesAfter);
    await resolveCascades(swapped);

    setBusy(false);
    if (movesAfter === 0) setFinished(true);
  };

  const handleTap = (index) => {
    if (busy || finished) return;

    if (selected === null) {
      setSelected(index);
      playHaptic('light');
      playCue('tick');
      return;
    }

    if (selected === index) {
      setSelected(null);
      playCue('whisper');
      return;
    }

    if (!isAdjacent(selected, index)) {
      setSelected(index);
      playHaptic('light');
      playCue('tick');
      return;
    }

    const from = selected;
    setSelected(null);
    trySwap(from, index);
  };

  const handlePointerDown = (index) => (event) => {
    if (busy || finished) return;
    dragRef.current = { index, x: event.clientX, y: event.clientY, swiped: false };
    // Keep move events on the origin tile even when the finger leaves it
    if (event.currentTarget.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // pointer may already be released
      }
    }
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.swiped || busy || finished) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_THRESHOLD) return;
    drag.swiped = true;
    const target = neighborByDelta(drag.index, dx, dy);
    if (target === null) return;
    setSelected(null);
    trySwap(drag.index, target);
  };

  const handlePointerUp = (index) => () => {
    const drag = dragRef.current;
    dragRef.current = null;
    // A short press without movement is still a tap-to-select
    if (drag && !drag.swiped) handleTap(index);
  };

  const handlePointerCancel = () => {
    dragRef.current = null;
  };

  const movesAreLow = !finished && movesLeft <= 5;

  return (
    <div className="app">
      <header className="screen-header">
        <button
          type="button"
          className="icon-btn"
          data-cuelume-press
          data-cuelume-release="droplet"
          onClick={() => {
            playHaptic('selection');
            onExit();
          }}
          aria-label="Назад"
        >
          <img src={img('icon-arrow-left.svg')} alt="" draggable="false" />
        </button>
        <h1 className="screen-title">Три в ряд</h1>
        <span className="icon-btn static" aria-hidden="true">
          <img src={img('icon-categories.svg')} alt="" draggable="false" />
        </span>
      </header>

      <div className="score-panel">
        <div className="score-cell">
          <span className="score-value">{score}</span>
          <span className="score-caption">Очки</span>
        </div>
        <span className="score-divider" aria-hidden="true" />
        <div className="score-cell">
          <span className={`score-value${movesAreLow ? ' low' : ''}`}>{movesLeft}</span>
          <span className="score-caption">Ходы</span>
        </div>
      </div>

      <main className="m3-board" aria-label="Игровое поле">
        {board.map((tile, index) => (
          <button
            key={tile.uid}
            type="button"
            className={[
              'm3-tile',
              tile.drop != null ? 'drop' : '',
              selected === index ? 'selected' : '',
              clearing.has(index) ? 'clearing' : '',
            ].join(' ')}
            style={{
              '--i': index,
              '--row': Math.floor(index / SIZE),
              '--col': index % SIZE,
              '--drop': tile.drop != null ? tile.drop : 0,
            }}
            onPointerDown={handlePointerDown(index)}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp(index)}
            onPointerCancel={handlePointerCancel}
            onClick={(event) => {
              // Pointer events cover mouse and touch; detail === 0 means a keyboard "click"
              if (event.detail === 0) handleTap(index);
            }}
            aria-label="Фишка"
          >
            <img src={tile.src} alt="" draggable="false" />
          </button>
        ))}
      </main>

      {finished && (
        <div className="result" role="dialog" aria-label="Результат игры">
          <div className="result-card">
            <ResultSticker won />
            <p className="result-title">Ходы закончились</p>
            <p className="result-score">{score}</p>
            <p className="result-score-label">очков</p>
            <div className="result-stats">
              <div className="stat">
                <span className="stat-label">Ходы</span>
                <span className="stat-value">{MOVES_LIMIT}</span>
              </div>
              <div className="stat">
                <span className="stat-label">За ход</span>
                <span className="stat-value">{Math.round(score / MOVES_LIMIT)}</span>
              </div>
            </div>
            <ResultActions onRestart={restart} onHome={onExit} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Match3;
